import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("dh_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.warn("auth refresh failed, clearing token", err);
      localStorage.removeItem("dh_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("dh_token", data.token);
    await refresh();
    return data;
  }, [refresh]);

  const register = useCallback(async (name, email, password, extra = {}) => {
    const { data } = await api.post("/auth/register", { name, email, password, ...extra });
    localStorage.setItem("dh_token", data.token);
    await refresh();
    return data;
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (logoutErr) {
      console.warn("server logout failed (continuing with client clear)", logoutErr);
    }
    localStorage.removeItem("dh_token");
    setUser(null);
  }, []);

  const toggleBookmark = useCallback(async (dealId) => {
    if (!user) throw new Error("Login required");
    const { data } = await api.post(`/bookmarks/${dealId}`);
    setUser((u) => (u ? { ...u, bookmarks: data.bookmarks } : u));
    return data;
  }, [user]);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, toggleBookmark, refresh, formatApiError }),
    [user, loading, login, register, logout, toggleBookmark, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
